<template>
  <div class="page-outer">
    <div class="page-header page-header-fixed-form">
      <div class="header-left">
        <h1 class="page-title">{{ pageTitle }}</h1>
                <div v-if="limitInfo" class="limit-info" :class="{ 'limit-reached': !limitInfo.canCreate }">
                  <i class="ri-user-line"></i>
                  <span class="limit-text">
                    已使用 {{ limitInfo.currentCount }}{{ limitInfo.maxStaffAccounts !== null ? ` / ${limitInfo.maxStaffAccounts}` : '' }} 個員工帳號
                  </span>
                  <span v-if="limitInfo.maxStaffAccounts !== null" class="remaining" :class="{ 'limit-reached': !limitInfo.canCreate }">
                    {{ limitInfo.canCreate ? `（剩餘 ${limitInfo.maxStaffAccounts - limitInfo.currentCount}）` : '（已達上限）' }}
                  </span>
                </div>
      </div>
      <div class="header-actions">
        <button
                v-if="isClientOwner && limitInfo?.canCreate"
                class="btn btn-primary"
                @click="handleCreate"
              >
                <i class="ri-add-line"></i>
                新增員工
              </button>
        <button
                v-else-if="isClientOwner && limitInfo && !limitInfo.canCreate"
                class="btn btn-primary"
                disabled
                :title="`已達員工帳號上限（${limitInfo.maxStaffAccounts}）`"
              >
                <i class="ri-add-line"></i>
                新增員工
              </button>
      </div>
    </div>

    <div class="page-container">
    <div class="page-toolbar">
      <div class="search-box">
        <input
          v-model="searchQuery"
          type="text"
          class="form-control"
          placeholder="搜尋登入帳號、Email 或姓名..."
          @input="handleSearch"
        />
        <i class="ri-search-line"></i>
      </div>
      <div class="filter-group">
        <select v-model="filterStatus" class="form-control" @change="handleFilterChange">
          <option value="">全部狀態</option>
          <option value="active">啟用</option>
          <option value="inactive">停用</option>
          <option value="suspended">暫停</option>
        </select>
      </div>
    </div>

    <div v-if="loading" class="loading-state">
      <div class="spinner"></div>
      <p>載入中...</p>
    </div>

    <div v-else-if="error" class="error-state">
      <p class="error-message">{{ error }}</p>
      <button class="btn btn-secondary" @click="fetchAccounts">重試</button>
    </div>

    <div v-else class="table-wrapper">
      <table>
        <thead>
          <tr>
            <th>登入帳號</th>
            <th>Email</th>
            <th>姓名</th>
            <th>角色</th>
            <th>權限</th>
            <th>門市範圍</th>
            <th>狀態</th>
            <th>建立時間</th>
            <th v-if="isClientOwner">操作</th>
          </tr>
        </thead>
        <tbody>
          <tr v-if="accounts.length === 0">
            <td :colspan="isClientOwner ? 8 : 7" class="empty-state">
              沒有找到員工帳號
            </td>
          </tr>
          <tr v-for="account in accounts" :key="account.id">
            <td>{{ account.username }}</td>
            <td>{{ account.email }}</td>
            <td>{{ account.name || '-' }}</td>
            <td>
              <span class="badge badge-info">{{ getAccountRoleLabel(account.role || 'client_staff', account.roleName) }}</span>
            </td>
            <td>
              <span
                :class="account.hasAllPermissions ? 'badge badge-success' : 'badge badge-warning'"
              >
                {{ account.hasAllPermissions ? '全部權限' : '部分權限' }}
              </span>
              <div v-if="!account.hasAllPermissions && account.allowedModules?.length" class="allowed-modules">
                {{ account.allowedModules.join(', ') }}
              </div>
            </td>
            <td>
              {{ getStoreLabel(account) }}
            </td>
            <td>
              <span :class="getStatusBadgeClass(account.status)">
                {{ getStatusText(account.status) }}
              </span>
            </td>
            <td>{{ formatDate(account.createdAt) }}</td>
            <td v-if="isClientOwner">
              <div class="action-buttons">
                <button
                  class="btn btn-sm btn-secondary"
                  @click="handleEdit(account)"
                  title="編輯"
                >
                  <i class="ri-edit-line"></i>
                </button>
                <button
                  class="btn btn-sm btn-danger"
                  @click="handleDelete(account)"
                  title="刪除"
                >
                  <i class="ri-delete-bin-line"></i>
                </button>
              </div>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <ListPagination
      v-if="total > 0"
      :page="currentPage"
      :total-pages="totalPages"
      :total="total"
      @change="goToPage"
    />
    </div>
  </div>
</template>

<script>
import { ref, computed, onMounted } from 'vue';
import { useRouter } from 'vue-router';
import { usePageTitle } from '@/composables/usePageTitle.js';
import { staffAccountService } from '@/api/modules/staffAccounts.js';
import { storeService } from '@/api/modules/stores.js';
import authService from '@/helpers/authservice/authService.js';
import { isClientOwner as userIsClientOwner } from '@/utils/clientAccount.js';
import { showSuccess, showError, showDeleteConfirm } from '@/utils/swal.js';
import { getAccountRoleLabel } from '@/utils/accountRoleLabel.js';

export default {
  name: 'ClientStaffAccounts',
  setup() {
    const router = useRouter();
    const { pageTitle } = usePageTitle('員工帳號管理');
    const accounts = ref([]);
    const loading = ref(false);
    const error = ref(null);
    const searchQuery = ref('');
    const filterStatus = ref('');
    const currentPage = ref(1);
    const pageSize = ref(20);
    const total = ref(0);
    const searchTimeout = ref(null);
    const storeMap = ref({});
    const limitInfo = ref(null);

    const isClientOwner = computed(() => userIsClientOwner(authService.getCurrentUser()));

    const totalPages = computed(() => {
      return Math.max(1, Math.ceil(total.value / pageSize.value));
    });

    const fetchAccounts = async () => {
      loading.value = true;
      error.value = null;

      try {
        const params = {
          page: currentPage.value,
          pageSize: pageSize.value,
        };

        if (searchQuery.value) {
          params.search = searchQuery.value;
        }

        if (filterStatus.value) {
          params.status = filterStatus.value;
        }

        const response = await staffAccountService.getAccounts(params);

        if (response && response.success && response.data) {
          accounts.value = response.data.items || [];
          total.value = response.data.total || 0;
          limitInfo.value = response.data.limitInfo || null;
        } else {
          error.value = (response && response.message) || '取得員工帳號列表失敗';
        }
      } catch (err) {
        console.error('取得員工帳號列表失敗:', err);
        error.value = err.message || '取得員工帳號列表失敗';
      } finally {
        loading.value = false;
      }
    };

    const fetchStores = async () => {
      try {
        const allStores = [];
        let page = 1;
        const pageSize = 100; // API 限制 pageSize <= 100
        let hasMore = true;

        while (hasMore) {
          const response = await storeService.getStores({ page, pageSize, status: 'active' });
          if (response && response.success && response.data) {
            const items = response.data.items || [];
            allStores.push(...items);
            const total = response.data.pagination?.totalItems || 0;
            const fetched = page * pageSize;
            hasMore = fetched < total && items.length > 0;
            page += 1;
          } else {
            hasMore = false;
          }
        }

        const map = {};
        allStores.forEach((store) => {
          map[store.id] = store.storeName || store.storeCode || store.id;
        });
        storeMap.value = map;
      } catch (err) {
        console.error('載入門市失敗:', err);
      }
    };

    const handleSearch = () => {
      if (searchTimeout.value) {
        clearTimeout(searchTimeout.value);
      }
      searchTimeout.value = setTimeout(() => {
        currentPage.value = 1;
        fetchAccounts();
      }, 400);
    };

    const handleFilterChange = () => {
      currentPage.value = 1;
      fetchAccounts();
    };

    const goToPage = (page) => {
      if (page >= 1 && page <= totalPages.value) {
        currentPage.value = page;
        fetchAccounts();
      }
    };

    const handleCreate = () => {
      router.push('/client/staff/accounts/create');
    };

    const handleEdit = (account) => {
      router.push(`/client/staff/accounts/${account.id}/edit`);
    };

    const handleDelete = async (account) => {
      const confirmed = await showDeleteConfirm(account.username || account.email);
      if (!confirmed) return;

      const originalLoading = loading.value;
      loading.value = true;
      error.value = null;

      try {
        const response = await staffAccountService.deleteAccount(account.id);
        if (response && response.success) {
          await showSuccess('刪除成功', `員工帳號「${account.username}」已刪除`);

          if (accounts.value.length === 1 && currentPage.value > 1) {
            currentPage.value = currentPage.value - 1;
          }

          await fetchAccounts();
        } else {
          const errorMsg = (response && response.message) || '刪除員工帳號失敗';
          await showError('刪除失敗', errorMsg);
        }
      } catch (err) {
        console.error('刪除員工帳號失敗:', err);
        await showError('刪除失敗', err.message || '刪除員工帳號失敗');
      } finally {
        loading.value = originalLoading;
      }
    };

    const getStoreLabel = (account) => {
      if (account.allowedStores === null || account.allowedStores === undefined) {
        return '全部門市';
      }
      if (!account.allowedStores.length) {
        return '未指定';
      }
      const names = account.allowedStores
        .map((id) => storeMap.value[id])
        .filter(Boolean);
      if (names.length > 0) {
        return names.join(', ');
      }
      return `指定 ${account.allowedStores.length} 門市`;
    };

    const formatDate = (dateString) => {
      if (!dateString) return '-';
      const date = new Date(dateString);
      return date.toLocaleString('zh-TW', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
      });
    };

    const getStatusBadgeClass = (status) => {
      const map = {
        active: 'badge badge-success',
        inactive: 'badge badge-danger',
        suspended: 'badge badge-warning',
      };
      return map[status] || 'badge badge-secondary';
    };

    const getStatusText = (status) => {
      const map = {
        active: '啟用',
        inactive: '停用',
        suspended: '暫停',
      };
      return map[status] || status;
    };

    onMounted(() => {
      fetchAccounts();
      fetchStores();
    });

    return {
      accounts,
      loading,
      error,
      searchQuery,
      filterStatus,
      currentPage,
      totalPages,
      total,
      pageTitle,
      isClientOwner,
      limitInfo,
      handleSearch,
      handleFilterChange,
      goToPage,
      handleCreate,
      handleEdit,
      handleDelete,
      formatDate,
      getStatusBadgeClass,
      getStatusText,
      fetchAccounts,
      getStoreLabel,
      getAccountRoleLabel,
    };
  },
};
</script>

<style scoped>
@import '~@/assets/scss/components/_list-page-common.scss';

.table-wrapper table {
  min-width: 1100px;
}

.allowed-modules {
  margin-top: 6px;
  color: #555;
  font-size: 12px;
}
</style>
